import { Subscription } from 'rxjs';
// Angular
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, effect, inject, signal } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
// Layout
import { LayoutConfigService, SplashScreenService } from './core/_base/layout';
import { SplashComponent } from './layout/splash/splash.component';
import { OnboardingService } from '@core/onboarding/onboarding.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'body[kt-root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  // CV-CLI-03 — convertido em 29/08/2026. Este componente NÃO tem uma única
  // ligação no template (`<mg-splash>` + `<router-outlet>`; `loader` e `title`
  // não são lidos), então por si só ele sempre foi `OnPush` trivial. Ele seguia
  // `Eager` por causa de um DESCENDENTE: o `DashboardComponent`, que nada
  // marcava e por isso dependia da travessia global descer por
  // `AppComponent` → `TenantComponent` → `LayoutComponent`. O painel passou a se
  // marcar sozinho (o `isFetching` do swr virou `signal`) — ver
  // `dashboard.deteccao.spec.ts` e `tenant.deteccao.spec.ts`.
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SplashComponent, RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private layoutConfigService = inject(LayoutConfigService);
  private splashScreenService = inject(SplashScreenService);
  private onboarding = inject(OnboardingService);
  /** Alguma navegação já terminou: há tela para mostrar no lugar da splash. */
  private navegou = signal(false);
  /** Leitura do roteiro que não responde não pode prender a splash para sempre. */
  private esperaMaxima = signal(false);
  private prazoDaSplash?: ReturnType<typeof setTimeout>;

  // Public properties
  title = 'Metronic';
  loader: boolean;
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  /**
   * Component constructor
   *
   * @param router: Router
   * @param layoutConfigService: LayoutConfigService
   * @param splashScreenService: SplashScreenService
   */
  constructor() {
    // O roteiro decide a primeira tela: sem esperar por ele, a splash sai sobre
    // o "Recuperando seu roteiro" ou sobre o painel que logo será trocado.
    effect(() => {
      if (this.navegou() && (!this.onboarding.abrindo() || this.esperaMaxima())) this.splashScreenService.hide();
    });
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit(): void {
    // enable/disable loader
    this.loader = this.layoutConfigService.getConfig('page-loader.type');

    const routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.liberarSplash();

        // scroll to top on every route change
        window.scrollTo(0, 0);

        this.revelarConteudo();
        return;
      }

      // BUG-CLI-1808: navegação recusada por um guard (`NavigationCancel`) ou
      // rota que não carregou (`NavigationError`, ex.: chunk lazy publicado por
      // cima de quem já estava dentro) NÃO produzem `NavigationEnd`. Sem tirar a
      // splash aqui, ela cobre a aplicação inteira para sempre — sem mensagem e
      // sem nada clicável. Não rola para o topo de propósito: a navegação não
      // aconteceu, a tela é a mesma.
      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.liberarSplash();
        this.revelarConteudo();
      }
    });
    this.unsubscribe.push(routerSubscription);
  }

  private liberarSplash(): void {
    if (this.navegou()) return;
    this.navegou.set(true);
    this.prazoDaSplash = setTimeout(() => this.esperaMaxima.set(true), 10000);
  }

  /** Libera o corpo da página — é a classe que o CSS usa para revelar o conteúdo. */
  private revelarConteudo(): void {
    setTimeout(() => {
      document.body.classList.add('page-loaded');
    }, 500);
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.unsubscribe.forEach(sb => sb.unsubscribe());
    clearTimeout(this.prazoDaSplash);
  }
}
